import React from 'react';
import { Trash2, CheckCircle, Bell, Phone, XCircle } from 'lucide-react';

const InquiriesTable = ({ studentLeads, loading, handleUpdateStatus, handleDeleteLead, mode = 'Admission' }) => {
  const [subjectFilter, setSubjectFilter] = React.useState('All');
  const [collegeFilter, setCollegeFilter] = React.useState('All');

  const filteredLeads = studentLeads.filter(lead => {
    const subjectMatch = subjectFilter === 'All' || lead.program === subjectFilter;
    const collegeMatch = collegeFilter === 'All' || lead.college === collegeFilter;
    return subjectMatch && collegeMatch;
  });

  const subjects = ['All', ...new Set(studentLeads.map(l => l.program).filter(Boolean))];
  const colleges = ['All', ...new Set(studentLeads.map(l => l.college).filter(Boolean))];

  const getStatusBadge = (status) => {
    const isMsg = mode === 'Message';
    const statusConfig = {
      'New': { bg: 'bg-blue-100', text: 'text-blue-800', icon: Bell, label: 'New' },
      'Contacted': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Phone, label: isMsg ? 'Processing' : 'Contacted' },
      'Enrolled': { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: isMsg ? 'Resolved' : 'Enrolled' },
      'Rejected': { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Closed' }
    };
    const config = statusConfig[status] || statusConfig['New'];
    const StatusIcon = config.icon || Bell;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${config.bg} ${config.text}`}>
        <StatusIcon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* Filter Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <div className="flex items-center space-x-6">
            <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">Filter by Institution</label>
                <select 
                    value={collegeFilter} 
                    onChange={(e) => setCollegeFilter(e.target.value)}
                    className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs font-bold outline-none focus:border-[#cc0000] min-w-[150px]"
                >
                    {colleges.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>
            <div className="space-y-1">
                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">{mode === 'Message' ? 'Filter by Subject' : 'Filter by Program'}</label>
                <select 
                    value={subjectFilter} 
                    onChange={(e) => setSubjectFilter(e.target.value)}
                    className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-xs font-bold outline-none focus:border-[#cc0000] min-w-[150px]"
                >
                    {subjects.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
            </div>
        </div>
        <div className="flex items-center justify-end">
            <div className="text-[10px] font-black uppercase text-slate-400 tracking-widest bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
                Found {filteredLeads.length} total {mode === 'Message' ? 'messages' : 'records'}
            </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-sm shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#cc0000] mx-auto"></div>
            <p className="mt-4 text-slate-500 font-bold uppercase text-[10px] tracking-widest">Loading Records...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">Sender / Student</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">Target Institution</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">{mode === 'Message' ? 'Subject' : 'Program'}</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">Inquiry Message</th>
                  <th className="px-6 py-5 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-5 text-right text-[10px] font-black text-slate-500 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredLeads.length === 0 ? (
                    <tr>
                        <td colSpan="6" className="p-20 text-center text-slate-400 font-bold uppercase text-[11px] tracking-widest">No matching records available</td>
                    </tr>
                ) : filteredLeads.map((lead) => (
                  <tr key={lead._id} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-5">
                      <div className="font-bold text-slate-900 group-hover:text-[#cc0000] transition-colors">{lead.name}</div>
                      <div className="text-[11px] text-slate-500 font-medium">{lead.email}</div>
                      <div className="text-[11px] text-[#cc0000] font-black tracking-widest">{lead.phone}</div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="text-[11px] font-black text-slate-600 uppercase italic">
                          {lead.college || '---'}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-[10px] font-black text-[#003366] uppercase bg-blue-50/50 px-3 py-1 rounded-sm border border-blue-100/50">
                          {lead.program}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="text-[11px] text-slate-600 max-w-xs font-semibold" title={lead.message}>
                          {lead.message || <span className="text-slate-300 italic font-normal">No message included</span>}
                      </div>
                    </td>
                    <td className="px-6 py-5">{getStatusBadge(lead.status)}</td>
                    <td className="px-6 py-5">
                      <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        {lead.status !== 'Contacted' && (
                          <button 
                              onClick={() => handleUpdateStatus(lead._id, 'Contacted')}
                              className="bg-white p-2 border border-slate-100 rounded-sm text-amber-600 hover:bg-amber-600 hover:text-white transition-all shadow-sm" 
                              title={mode === 'Message' ? 'Start Processing' : 'Mark Contacted'}
                          >
                              <Phone className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {lead.status !== 'Enrolled' && (
                          <button 
                              onClick={() => handleUpdateStatus(lead._id, 'Enrolled')}
                              className="bg-white p-2 border border-slate-100 rounded-sm text-green-600 hover:bg-green-600 hover:text-white transition-all shadow-sm" 
                              title={mode === 'Message' ? 'Mark Resolved' : 'Mark Enrolled'}
                          >
                              <CheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button 
                          onClick={() => handleDeleteLead(lead._id)}
                          className="bg-white p-2 border border-slate-100 rounded-sm text-red-600 hover:bg-red-600 hover:text-white transition-all shadow-sm" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InquiriesTable;

